/**
 * Логика электрики: элемент по id и проверки вида — по енаму каталога, не по строкам.
 *
 * Проверки принимают любой элемент каталога (так их вызывает общий код холста и
 * панелей, у которого в руках `CatalogItem`) и смотрят вид в разделе электрики по id.
 */
import type { CatalogItem } from "../../types";
import { electricalSection } from "./catalog";
import { ElectricalKind, type ElectricalCatalogItem } from "./types";
import { ElectricalId } from "./ids";

export const SOCKET_BLOCK_IDS = [
	ElectricalId.Socket,
	ElectricalId.SocketDouble,
	ElectricalId.SocketTriple,
	ElectricalId.SocketQuadruple,
	ElectricalId.SocketQuintuple,
] as const;
export const socketBlockCount = (id: string): number | null => {
	const index = SOCKET_BLOCK_IDS.findIndex((candidate) => candidate === id);
	return index < 0 ? null : index + 1;
};
export const switchKeyCount = (id: string): 1 | 2 | 3 =>
	id === ElectricalId.SwitchTriple ? 3 : id === ElectricalId.SwitchDouble ? 2 : 1;

export const electricalItemById = (id: string): ElectricalCatalogItem | null =>
	electricalSection.items.find((item) => item.id === id) ?? null;

/** Вид электрического элемента; null — это не электрика или тип удалён из каталога. */
export const electricalKindOf = (item: CatalogItem | null): ElectricalKind | null =>
	item ? (electricalItemById(item.id)?.kind ?? null) : null;

export const isSocket = (item: CatalogItem | null): boolean => {
	const kind = electricalKindOf(item);
	return kind === ElectricalKind.Socket || kind === ElectricalKind.LowVoltage;
};
export const isSwitch = (item: CatalogItem | null): boolean =>
	electricalKindOf(item) === ElectricalKind.Switch;
export const isJunctionBox = (item: CatalogItem | null): boolean =>
	electricalKindOf(item) === ElectricalKind.JunctionBox;
export const isPanel = (item: CatalogItem | null): boolean =>
	electricalKindOf(item) === ElectricalKind.Panel;
export const isLowVoltagePanel = (item: CatalogItem | null): boolean =>
	electricalKindOf(item) === ElectricalKind.LowVoltagePanel;
/** Узел проводки — распредкоробка или щиток: к нему подключают, сам он «без кабеля» не бывает. */
export const isWiringHub = (item: CatalogItem | null): boolean =>
	isJunctionBox(item) || isPanel(item) || isLowVoltagePanel(item);
